import React, { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import { Button, useTheme } from '@mui/material'
import SettingsIcon from '@mui/icons-material/Settings'
import KeyIcon from '@mui/icons-material/Key'
import { useNavigate } from 'react-router-dom'
import {
  browserSupportsWebAuthn,
  startAuthentication,
} from '@simplewebauthn/browser'
import MFACodeInput from '../../components/TextInputs/MFACodeInput'
import API from '../../services/API'
import NotAuthorised from '../../components/NotAuthorised'

const MFALogin = ({ onSuccess, onFail }) => {
  const [attemptsRemaining, setAttemptsRemaining] = useState(3)
  const [code, setCode] = useState('')
  const [showErrors, setShowErrors] = useState(false)
  const navigate = useNavigate()
  const [submitting, setSubmitting] = useState(false)
  const [hasPasskeys, setHasPasskeys] = useState(false)
  const [passkeyBusy, setPasskeyBusy] = useState(false)
  const [passkeyError, setPasskeyError] = useState(false)

  const submitForm = () => {
    setSubmitting(true)
    API.post('/auth/mfa-check/', { token: code }).then((res) => {
      if (res.status === 200) {
        onSuccess()
      } else {
        if (attemptsRemaining === 1) {
          API.delete('/logout')
          window.location.reload()
        } else {
          setAttemptsRemaining((prev) => prev - 1)
          setSubmitting(false)
        }
      }
    })
  }

  useEffect(() => {
    if (code.length === 6) {
      const timer = setTimeout(() => submitForm(), 300)
      return () => clearTimeout(timer)
    }
  }, [code])

  useEffect(() => {
    if (!browserSupportsWebAuthn()) return
    API.get('/auth/passkey/list').then((res) => {
      if (res.status === 200 && res.data?.passkeys?.length) {
        setHasPasskeys(true)
      }
    })
  }, [])

  const handlePasskey = async () => {
    setPasskeyBusy(true)
    setPasskeyError(false)
    try {
      const optionsRes = await API.post('/auth/passkey/authentication-options')
      if (optionsRes.status !== 200) throw new Error('optionsFailed')
      const authResponse = await startAuthentication({
        optionsJSON: optionsRes.data,
      })
      const verifyRes = await API.post('/auth/passkey/verify', {
        response: authResponse,
      })
      if (verifyRes.status !== 200) throw new Error('verifyFailed')
      onSuccess()
    } catch (e) {
      // A cancelled browser prompt lands here too — fall back to the code
      // input without burning a TOTP attempt.
      if (e?.name !== 'NotAllowedError') {
        setPasskeyError(true)
      }
      setPasskeyBusy(false)
    }
  }

  return (
    <NotAuthorised
      header="Two Factor Authentication"
      headerCaption="Enter the 6-digit code from your authentication app"
    >
      <Stack spacing={3} alignItems="center">
        <MFACodeInput
          value={code}
          onChange={setCode}
          disabled={submitting || passkeyBusy}
        />

        <Button
          onClick={submitForm}
          variant="contained"
          fullWidth
          disabled={code.length < 6 || submitting || passkeyBusy}
          sx={{
            borderRadius: 3,
            py: 1.2,
            fontWeight: 600,
            textTransform: 'none',
          }}
        >
          {submitting ? 'Verifying...' : 'Verify'}
        </Button>

        {hasPasskeys && (
          <>
            <Divider flexItem>or</Divider>
            <Button
              onClick={handlePasskey}
              variant="outlined"
              fullWidth
              disabled={submitting || passkeyBusy}
              startIcon={<KeyIcon />}
              sx={{
                borderRadius: 3,
                py: 1.2,
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              {passkeyBusy ? 'Waiting for passkey...' : 'Use a passkey'}
            </Button>
          </>
        )}

        {passkeyError && (
          <Alert severity="error">
            Passkey verification failed. Please try again or enter your code.
          </Alert>
        )}

        {attemptsRemaining < 3 && (
          <Alert severity="error">{attemptsRemaining} attempts remaining</Alert>
        )}

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            color="contrast"
            onClick={() => navigate('/my-account/2fa')}
            startIcon={<SettingsIcon />}
          >
            Manage 2FA
          </Button>
        </Stack>
      </Stack>
    </NotAuthorised>
  )
}

export default MFALogin
